import React from 'react';
import PropTypes from 'prop-types';

export const buttonTypes = {
  primary: 'primary',
  secondary: 'secondary',
  secondaryReversed: 'secondary-reversed',
  warning: 'warning',
  danger: 'danger',
  success: 'success',
  blue: 'blue',
  blueReversed: 'blue-reversed',
  noButton: 'no-button',
  noButtonText: 'no-button-text',
};

export const buttonSizes = {
  extraSmall: 'button-xsm',
  small: 'button-sm',
  medium: 'button-med',
  large: 'button-lg',
  extraLarge: 'button-xlg',
};

export const buttonStyles = {
  flex: 'button-flex',
  flexAround: 'button-flex-around',
  flexBetween: 'button-flex-between',
  tabs: 'button-tabs',
};

export const buttonTextSizes = {
  extraSmall: 'text-smallest',
  small: 'text-small',
  mediumSmall: 'text-medium-small',
  medium: 'text-medium',
  large: 'text-large',
  extraLarge: 'text-extra-large',
};

export const buttonFunctions = {
  button: 'button',
  submit: 'submit',
  reset: 'reset',
};

/**
 * A generic button component
 * @param {*} text - Content to put in the button. Generally this will be text but can also include components such as internationalized text
 * @param {string} title - The title of the button
 * @param {function} onClick - The function to be executed when the button clicks
 * @param {boolean} disabled - Indicates if the button should be displayed as disabled
 * @param {string} buttonType - The general style of button to be displayed
 * @param {string} buttonSize - The size of the button to be displayed (primarily affects padding)
 * @param {string} buttonName - The name of the button
 * @param {string} buttonTextSize - The text size of the button
 * @param {string} additionalStyles - Additional CSS styles to be applied to the button
 * @param {string} buttonStyle - Additional button display styles
 * @param {string} value - The value for the button
 * @param {object} leftIcon - The component to display as an icon left of the button contents
 * @param {string} leftIconStyle - Styles to be applied to the front icon
 * @param {object} rightIcon - The component to display as an icon right of the button contents
 * @param {string} rightIconStyle - Styles to be applied to the back icon
 * @param {string} buttonFunction - The function of the button as a button, submit, or reset
 */
export const Button = (props) => {
  const {
    text,
    title,
    onClick,
    disabled,
    buttonType,
    buttonSize,
    buttonName,
    buttonTextSize,
    additionalStyles,
    buttonStyle,
    value,
    leftIcon,
    leftIconStyle,
    rightIcon,
    rightIconStyle,
    buttonFunction,
    'data-test': dataTest,
  } = props;
  return (
    <button
      type={buttonFunction || buttonFunctions.button}
      disabled={disabled}
      onClick={onClick}
      title={title}
      className={
        'button-' +
        (buttonType || buttonTypes.primary) +
        ' ' +
        (buttonSize || buttonSizes.medium) +
        ' ' +
        (buttonTextSize || buttonTextSizes.medium) +
        ' ' +
        (additionalStyles || '') +
        ' ' +
        (buttonStyle || '')
      }
      name={buttonName}
      value={value}
       data-test={dataTest || 'button'}>
      <span className={leftIconStyle} data-test="left-icon">
        {leftIcon}
      </span>
      {text}
      <span className={rightIconStyle} data-test="right-icon">
        {rightIcon}
      </span>
    </button>
  );
};

Button.propTypes = {
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  buttonType: PropTypes.string,
  buttonSize: PropTypes.string,
  title: PropTypes.string,
  buttonName: PropTypes.string,
  buttonTextSize: PropTypes.string,
  additionalStyles: PropTypes.string,
  buttonStyle: PropTypes.string,
  value: PropTypes.string,
};

export default Button;
